import type { DocumentLink } from "@/models/document-link/DocumentLink";
import { RefType, type ExtraMetadata } from "@/models/document-link/DocumentLink";
import { isOtherSideHistorical } from "@/utils/documentLinkOtherSide";
import {
  buildWorkspaceReferenceVisibilityContext,
  filterWorkspaceReferenceLinksForDisplay,
} from "@/utils/documentLinkWorkspaceVersionCollapse";

/**
 * Same parsing as `DocumentReferencesTab` (string JSON, boolean coercion for historical flags).
 */
export function parseReferencesSidebarExtraMetadata(
  extraMetadata?: Record<string, string> | string
): ExtraMetadata {
  if (!extraMetadata) {
    return {};
  }

  let parsed: Record<string, unknown> = {};

  if (typeof extraMetadata === "object") {
    parsed = extraMetadata as Record<string, unknown>;
  } else if (typeof extraMetadata === "string") {
    try {
      parsed = JSON.parse(extraMetadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  } else {
    return {};
  }

  const result: ExtraMetadata = { ...parsed } as ExtraMetadata;

  if (typeof parsed.origin_is_historical === "string") {
    result.origin_is_historical = parsed.origin_is_historical === "true";
  }
  if (typeof parsed.destination_is_historical === "string") {
    result.destination_is_historical =
      parsed.destination_is_historical === "true";
  }

  return result;
}

function classifyOtherSide(
  link: DocumentLink,
  isOutgoing: boolean
): "legislation" | "workspace" {
  if (isOutgoing) {
    return link.destinationWorkspaceId ? "workspace" : "legislation";
  }
  return link.originWorkspaceId ? "workspace" : "legislation";
}

export type ReferencesSidebarExclusionReason =
  | "not_touching_document"
  | "internal_ref_type"
  | "historical_outgoing_destination"
  | "historical_incoming_origin"
  | "workspace_collapsed_to_latest_revision";

export interface ReferencesSidebarVisibilitySummary {
  documentId: string;
  totalFetched: number;
  /** Same as legislation + workspace rows in {@link DocumentReferencesTab} (filters + optional latest-revision collapse). */
  shownInSidebarCount: number;
  showHistorical: boolean;
  showEarlierWorkspaceVersions: boolean;
  /** Link ids grouped by first exclusion rule (aligned with `DocumentReferencesTab` filters). */
  excludedByReason: Record<ReferencesSidebarExclusionReason, string[]>;
  /** Ids visible in the sidebar (legislation + workspace after filters and workspace display rules). */
  shownLinkIds: string[];
}

/**
 * Explains which fetched document links are not counted in the references sidebar,
 * using the same rules as {@link DocumentReferencesTab} (filters; workspace list uses
 * {@link filterWorkspaceReferenceLinksForDisplay} when `showEarlierWorkspaceVersions` is false).
 */
export function summarizeReferencesSidebarVisibility(
  links: DocumentLink[],
  documentId: string,
  options: {
    showHistorical?: boolean;
    showEarlierWorkspaceVersions?: boolean;
  } = {}
): ReferencesSidebarVisibilitySummary {
  const showHistorical = options.showHistorical ?? true;
  const showEarlierWorkspaceVersions =
    options.showEarlierWorkspaceVersions ?? false;

  const excludedByReason: Record<ReferencesSidebarExclusionReason, string[]> =
    {
      not_touching_document: [],
      internal_ref_type: [],
      historical_outgoing_destination: [],
      historical_incoming_origin: [],
      workspace_collapsed_to_latest_revision: [],
    };

  const push = (reason: ReferencesSidebarExclusionReason, id: string) => {
    excludedByReason[reason].push(id);
  };

  const filtered: DocumentLink[] = [];

  for (const link of links) {
    const touchesOrigin = link.originDocumentId === documentId;
    const touchesDest = link.destinationDocumentId === documentId;
    if (!touchesOrigin && !touchesDest) {
      push("not_touching_document", link.id);
      continue;
    }

    const metadata = parseReferencesSidebarExtraMetadata(link.extraMetadata);
    if (metadata.ref_type === RefType.INTERNAL) {
      push("internal_ref_type", link.id);
      continue;
    }

    if (!showHistorical && isOtherSideHistorical(link, documentId)) {
      const isOutgoing = touchesOrigin;
      if (isOutgoing) {
        push("historical_outgoing_destination", link.id);
      } else {
        push("historical_incoming_origin", link.id);
      }
      continue;
    }

    filtered.push(link);
  }

  const legislation: DocumentLink[] = [];
  const incomingWorkspace: DocumentLink[] = [];
  const outgoingWorkspace: DocumentLink[] = [];
  for (const link of filtered) {
    const isOutgoing = link.originDocumentId === documentId;
    if (classifyOtherSide(link, isOutgoing) === "workspace") {
      if (isOutgoing) {
        outgoingWorkspace.push(link);
      } else {
        incomingWorkspace.push(link);
      }
    } else {
      legislation.push(link);
    }
  }

  const workspaceVisibilityContext = buildWorkspaceReferenceVisibilityContext(
    [...incomingWorkspace, ...outgoingWorkspace],
    documentId
  );
  const incomingWorkspaceDisplay = filterWorkspaceReferenceLinksForDisplay(
    incomingWorkspace,
    documentId,
    showEarlierWorkspaceVersions,
    workspaceVisibilityContext
  );
  const outgoingWorkspaceDisplay = filterWorkspaceReferenceLinksForDisplay(
    outgoingWorkspace,
    documentId,
    showEarlierWorkspaceVersions,
    workspaceVisibilityContext
  );

  const markCollapsed = (all: DocumentLink[], shown: DocumentLink[]) => {
    const ids = new Set(shown.map((l) => l.id));
    for (const link of all) {
      if (!ids.has(link.id)) {
        push("workspace_collapsed_to_latest_revision", link.id);
      }
    }
  };
  if (!showEarlierWorkspaceVersions) {
    markCollapsed(incomingWorkspace, incomingWorkspaceDisplay);
    markCollapsed(outgoingWorkspace, outgoingWorkspaceDisplay);
  }

  const shownLinkIds = [
    ...legislation.map((l) => l.id),
    ...incomingWorkspaceDisplay.map((l) => l.id),
    ...outgoingWorkspaceDisplay.map((l) => l.id),
  ];
  const shownInSidebarCount = shownLinkIds.length;

  return {
    documentId,
    totalFetched: links.length,
    shownInSidebarCount,
    showHistorical,
    showEarlierWorkspaceVersions,
    excludedByReason,
    shownLinkIds,
  };
}

export function formatReferencesSidebarVisibilityReport(
  s: ReferencesSidebarVisibilitySummary
): string {
  const lines: string[] = [];
  lines.push("=== References sidebar — fetched vs shown (DocumentReferencesTab rules) ===");
  lines.push(`documentId=${s.documentId}`);
  lines.push(
    `toggles: showHistorical=${s.showHistorical} | showEarlierWorkspaceVersions=${s.showEarlierWorkspaceVersions}`
  );
  lines.push(
    `totalFetched=${s.totalFetched} | shownInSidebar=${s.shownInSidebarCount} | notShown=${s.totalFetched - s.shownInSidebarCount}`
  );
  lines.push("");
  const reasons: ReferencesSidebarExclusionReason[] = [
    "not_touching_document",
    "internal_ref_type",
    "historical_outgoing_destination",
    "historical_incoming_origin",
    "workspace_collapsed_to_latest_revision",
  ];
  const labels: Record<ReferencesSidebarExclusionReason, string> = {
    not_touching_document: "neither origin nor destination matches open doc",
    internal_ref_type: "ref_type internal (hidden from sidebar)",
    historical_outgoing_destination:
      "outgoing to historical destination (Vis historiske off)",
    historical_incoming_origin:
      "incoming from historical origin (Vis historiske off)",
    workspace_collapsed_to_latest_revision:
      "workspace link hidden by latest-revision collapse (Vis tidligere versioner off)",
  };
  for (const r of reasons) {
    const ids = s.excludedByReason[r];
    if (ids.length === 0) continue;
    lines.push(`--- ${r} (${ids.length}) — ${labels[r]} ---`);
    for (const id of ids) {
      lines.push(`  ${id}`);
    }
  }
  return lines.join("\n");
}
